import { DomainError, TransportError } from "@econ-v1/domain";
import { sha256Hex } from "./sha256.js";
export function createBrowserStageCache(baseUrl) {
    return new VerifiedStageCache({
        baseUrl,
        cacheStorage: globalThis.caches,
        fetch: globalThis.fetch.bind(globalThis),
    });
}
const META_CACHE = "node-client-stage-meta";
const REGISTRY_URL = "https://node-client.invalid/stage-registry";
const ACTIVE_URL = "https://node-client.invalid/stage-active";
const STAGE_CACHE_PREFIX = "node-client-stage-";
export class VerifiedStageCache {
    #baseUrl;
    #cacheStorage;
    #fetch;
    #sha256;
    #promotionTail = Promise.resolve();
    constructor(options) {
        this.#baseUrl = new URL(options.baseUrl);
        this.#cacheStorage = options.cacheStorage;
        this.#fetch = options.fetch ?? fetch;
        this.#sha256 = options.sha256 ?? ((bytes) => sha256Hex(bytes));
    }
    async prepare(appName, manifest) {
        return await this.#exclusive(async () => await this.#prepareExclusive(appName, manifest));
    }
    async #exclusive(operation) {
        const previous = this.#promotionTail;
        let release = () => undefined;
        this.#promotionTail = new Promise((resolve) => {
            release = resolve;
        });
        await previous;
        try {
            return await operation();
        }
        finally {
            release();
        }
    }
    async #prepareExclusive(appName, manifest) {
        const snapshot = await this.read();
        const entry = snapshot?.entries.find((candidate) => candidate.appName === appName && sameManifest(candidate.manifest, manifest));
        if (entry === undefined) {
            throw new DomainError({ code: "not_found", details: { appName }, message: "Stage manifest is not available in the verified catalog" });
        }
        const active = await readActiveStageMap(this.#cacheStorage);
        const cacheName = stageCacheName(appName, entry.manifestSha256);
        const assetBaseUrl = resolveAssetBase(this.#baseUrl, entry.assetBase);
        const current = active[appName];
        if (current?.cacheName === cacheName &&
            current.assetBase === assetBaseUrl &&
            current.manifestSha256 === entry.manifestSha256 &&
            current.version === manifest.version) {
            return preparedStageAsset(appName, entry.assetBase, entry.manifestSha256, manifest, this.#baseUrl);
        }
        const pendingCacheName = pendingStageCacheName(appName, entry.manifestSha256);
        const nextActive = {
            ...active,
            [appName]: {
                appName,
                assetBase: assetBaseUrl,
                cacheName,
                manifestSha256: entry.manifestSha256,
                version: manifest.version,
            },
        };
        await this.#cacheStorage.delete(pendingCacheName);
        const pendingCache = await this.#cacheStorage.open(pendingCacheName);
        try {
            for (const [assetPath, expectedHash] of Object.entries(manifest.integrity)) {
                const url = assetUrl(this.#baseUrl, entry.assetBase, assetPath);
                const response = await this.#fetchVerified(url, expectedHash);
                await pendingCache.put(url, response);
            }
            await this.#cacheStorage.delete(cacheName);
            const versionCache = await this.#cacheStorage.open(cacheName);
            for (const request of await pendingCache.keys()) {
                const response = await pendingCache.match(request.url);
                if (response !== undefined)
                    await versionCache.put(request.url, response.clone());
            }
            await writeActiveStageMap(this.#cacheStorage, nextActive);
        }
        catch (error) {
            await bestEffortDelete(this.#cacheStorage, pendingCacheName);
            if (current?.cacheName !== cacheName)
                await bestEffortDelete(this.#cacheStorage, cacheName);
            throw error;
        }
        await bestEffortDelete(this.#cacheStorage, pendingCacheName);
        await bestEffortEvictInactiveStageCaches(this.#cacheStorage, nextActive);
        return preparedStageAsset(appName, entry.assetBase, entry.manifestSha256, manifest, this.#baseUrl);
    }
    async read() {
        const cache = await this.#cacheStorage.open(META_CACHE);
        const response = await cache.match(REGISTRY_URL);
        if (response === undefined)
            return undefined;
        return await response.clone().json();
    }
    async write(snapshot) {
        await this.#exclusive(async () => {
            const active = await readActiveStageMap(this.#cacheStorage);
            const installed = new Set(snapshot.entries.map((entry) => entry.appName));
            const nextActive = Object.fromEntries(Object.entries(active).filter(([appName]) => installed.has(appName)));
            if (Object.keys(nextActive).length !== Object.keys(active).length) {
                await writeActiveStageMap(this.#cacheStorage, nextActive);
            }
            const cache = await this.#cacheStorage.open(META_CACHE);
            await cache.put(REGISTRY_URL, jsonResponse(snapshot));
            await bestEffortEvictInactiveStageCaches(this.#cacheStorage, nextActive);
        });
    }
    async #fetchVerified(url, expectedHash) {
        let response;
        try {
            response = await this.#fetch(url);
        }
        catch {
            // #1529: this is the actual network-dependent step for a stage this device has
            // never opened before — `StageLoader#importModule` (client/ui) only ever imports
            // from an ALREADY-verified/cached URL this `prepare()` call produces, so a
            // never-cached stage fails HERE, not there. Before this fix the raw fetch
            // rejection (a plain TypeError, uninstantiable client-side across the MessagePort
            // boundary) crossed into `kernel-rpc-server.ts`'s `clientError()` catch-all and
            // came out the other side as a generic, unclassified `DomainError` ("Kernel RPC
            // request failed") — indistinguishable from a real bug. `navigator.onLine ===
            // false` (true in a Worker scope too) is the reliable signal a real disconnected
            // device gives; surface it as a `TransportError` so it deserializes intact across
            // the MessagePort round-trip (`deserializeClientError`/`isSerializableClientError`
            // preserve `kind: "transport"`) and `app-shell.ts`'s `isOfflineError` can route it
            // to the offline-aware, auto-recovering fallback instead of a dead end.
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                throw new TransportError({
                    code: "unavailable",
                    message: `Stage asset ${url} has never been cached on this device and can't be fetched offline`,
                });
            }
            throw new DomainError({ code: "not_found", message: `Stage asset ${url} could not be fetched` });
        }
        if (!response.ok) {
            throw new DomainError({ code: "not_found", message: `Stage asset ${url} could not be fetched` });
        }
        const bytes = new Uint8Array(await response.clone().arrayBuffer());
        const actualHash = await this.#sha256(bytes);
        if (actualHash !== expectedHash) {
            throw new DomainError({ code: "conflict", message: `Stage asset integrity mismatch for ${url}` });
        }
        return response;
    }
}
export async function resolveActiveStageAsset(cacheStorage, request) {
    const active = await readActiveStageMap(cacheStorage);
    const match = Object.values(active).find((pointer) => request.url.startsWith(pointer.assetBase));
    if (match === undefined)
        return undefined;
    const cache = await cacheStorage.open(match.cacheName);
    const response = await cache.match(request.url);
    return response?.clone();
}
async function evictInactiveStageCaches(cacheStorage, active) {
    const retained = new Set([META_CACHE, ...Object.values(active).map((pointer) => pointer.cacheName)]);
    for (const name of await cacheStorage.keys()) {
        if (!name.startsWith(STAGE_CACHE_PREFIX) || retained.has(name))
            continue;
        await cacheStorage.delete(name);
    }
}
async function bestEffortDelete(cacheStorage, name) {
    try {
        await cacheStorage.delete(name);
    }
    catch {
        // Cleanup must not roll back or invalidate an already committed active pointer.
    }
}
async function bestEffortEvictInactiveStageCaches(cacheStorage, active) {
    try {
        await evictInactiveStageCaches(cacheStorage, active);
    }
    catch {
        // Stale caches are safe to retain until a later successful promotion.
    }
}
function assetUrl(baseUrl, assetBase, assetPath) {
    return new URL(assetPath, resolveAssetBase(baseUrl, assetBase)).toString();
}
function pendingStageCacheName(appName, manifestSha256) {
    return `${STAGE_CACHE_PREFIX}pending-${appName}-${manifestSha256}`;
}
function preparedStageAsset(appName, assetBase, manifestSha256, manifest, baseUrl) {
    return {
        appName,
        entryUrl: assetUrl(baseUrl, assetBase, manifest.entry),
        manifestSha256,
        version: manifest.version,
    };
}
async function readActiveStageMap(cacheStorage) {
    const cache = await cacheStorage.open(META_CACHE);
    const response = await cache.match(ACTIVE_URL);
    if (response === undefined)
        return {};
    return await response.clone().json();
}
function resolveAssetBase(baseUrl, assetBase) {
    return new URL(assetBase, baseUrl).toString();
}
function stageCacheName(appName, manifestSha256) {
    return `${STAGE_CACHE_PREFIX}${appName}-${manifestSha256}`;
}
function stageManifestAssetBase(manifest) {
    const candidate = manifest.assetBase;
    return typeof candidate === "string" && candidate.length > 0 ? candidate : undefined;
}
async function writeActiveStageMap(cacheStorage, active) {
    const cache = await cacheStorage.open(META_CACHE);
    await cache.put(ACTIVE_URL, jsonResponse(active));
}
function jsonResponse(value) {
    return new Response(JSON.stringify(value), {
        headers: { "content-type": "application/json" },
    });
}
function sameManifest(left, right) {
    const leftAssetBase = stageManifestAssetBase(left);
    const rightAssetBase = stageManifestAssetBase(right);
    return (left.entry === right.entry &&
        left.version === right.version &&
        JSON.stringify(left.integrity) === JSON.stringify(right.integrity) &&
        JSON.stringify(left.requires) === JSON.stringify(right.requires) &&
        (leftAssetBase === rightAssetBase || leftAssetBase === undefined || rightAssetBase === undefined));
}
//# sourceMappingURL=stage-cache.js.map